import useTranslator from "../../hooks/useTranslator";
import { haveValue } from "../../helper/common";
import { useEffect, useState } from "react";
import FilePreviewModal from "../FilePreviewmodal";
import { Badge, Button, Card, Drawer, Icon, InfoList } from "../design";
import "./DataInfoModal.css";

const DataInfoModal = ({
  isOpen,
  onClose,
  infoModal,
  verifiedDocs,
  claveDocs,
  docsMap,
  forensicAnalysis,
  uploadedTenantDocs = [],
  hideDownload = false,
}) => {
  const { _t } = useTranslator();

  const [documentGalleryData, setDocumentGalleryData] = useState([]);
  const [filePreview, showFilePreview] = useState(false);
  const [showIndex, setShowIndex] = useState(null);

  const [currentLang, setCurrentLang] = useState(
    localStorage.getItem("i18nextLng").toLowerCase() || "en-us"
  );

  useEffect(() => {
    setCurrentLang(localStorage.getItem("i18nextLng").toLowerCase() || "en-us");
  }, [localStorage.getItem("i18nextLng")]);
  useEffect(() => {
    if (!isOpen) return;

    const uploadedDocsByFileName = {};
    uploadedTenantDocs.forEach((d) => {
      const fileName = d?.file_name || d?.url?.split("/")?.pop();
      if (fileName) {
        uploadedDocsByFileName[fileName] = d;
      }
    });

    const result = [];

    if (
      Array.isArray(infoModal?.foundInDocuments) &&
      docsMap &&
      verifiedDocs instanceof Map
    ) {
      infoModal.foundInDocuments.forEach((doc) => {
        const docMeta = docsMap[doc?.documentId ?? doc?.document_id];
        // Payslip instances share a document_id, but each file_url identifies
        // the actual uploaded document represented by this row.
        const url = doc?.file_url || doc?.fileUrl || doc?.url || docMeta?.url;
        if (!url) return;
        const uploadKey = url?.split("uploads/")[1];

        const isVerified = verifiedDocs.has(uploadKey);
        const isClave = claveDocs instanceof Map && claveDocs.has(uploadKey);

        const matchedUploaded = uploadedDocsByFileName[uploadKey];
        const signedUrl = matchedUploaded?.url || url;

        result.push({
          ...doc,
          url: signedUrl,
          file_name: uploadKey,
          tick: isVerified,
          clave: isClave,
        });
      });
    }

    setDocumentGalleryData(result);
  }, [infoModal, docsMap, verifiedDocs, claveDocs, isOpen, uploadedTenantDocs]);

  if (!isOpen) return null;

  const langKey = currentLang === "es" ? "es" : "en";
  const remarksText =
    typeof infoModal?.remarks === "object"
      ? infoModal?.remarks?.[langKey]
      : infoModal?.remarks;
  const remarksList = (remarksText?.split(". ") ?? [])
    .map((s) => s.trim())
    .filter(Boolean);

  const isVerifiedSource =
    infoModal?.verifiedSource === true ||
    (forensicAnalysis?.Income?.verifiedSource === true &&
      infoModal?.verified === true);

  const value = (() => {
    const val = haveValue(infoModal?.value?.[langKey])
      ? infoModal?.value?.[langKey]
      : haveValue(infoModal?.netIncome)
        ? `€${infoModal?.netIncome}`
        : "N/A";

    if (typeof val === "string" && val.includes(". ")) {
      return val.split(". ").map((part, index, array) => (
        <span key={index}>
          {part}
          {index < array.length - 1 ? "." : ""}
          {index < array.length - 1 && <br />}
        </span>
      ));
    }
    return val;
  })();

  return (
    <>
      <Drawer
        open={isOpen}
        onClose={onClose}
        title={_t("dataInformation")}
        closeLabel={_t("close")}
      >
        <div className="data-info">
          <Card
            as="div"
            title={
              haveValue(infoModal?.keyFactor)
                ? _t(infoModal?.keyFactor?.toLowerCase())
                : _t("payslip")
            }
            description={value}
            actions={
              isVerifiedSource && (
                <Badge tone="lime">
                  <Icon name="check" size={12} />
                  {_t("Verified")}
                </Badge>
              )
            }
          >
            <InfoList
              layout="stacked"
              emptyText="N/A"
              items={[
                {
                  label: _t("status"),
                  value:
                    infoModal?.consistentAcrossDocuments || // updated key
                    infoModal?.consitentAccrosDocuments || // for old users
                    (forensicAnalysis?.Income?.verifiedSource === true &&
                      infoModal?.verified)
                      ? _t("consistent")
                      : _t("notConsistent"),
                },
                {
                  label: _t("remarks"),
                  value: remarksList.length ? (
                    <ul className="data-info__remarks">
                      {remarksList.map((tag, i) => (
                        <li key={i}>{tag}</li>
                      ))}
                    </ul>
                  ) : null,
                },
              ]}
            />
          </Card>

          {documentGalleryData.length > 0 && (
            <Card as="div" title={_t("foundInDocuments")}>
              <div className="data-info__docs">
                {documentGalleryData?.map((doc, index) => (
                  <div
                    key={doc?.instance_id || doc?.file_url || index}
                    className="data-info__doc"
                  >
                    <Icon name="fileText" size={20} />
                    <Button
                      variant="link"
                      className="data-info__doc-name"
                      onClick={() => {
                        setShowIndex(index);
                        showFilePreview(true);
                      }}
                    >
                      {doc?.localDocumentName?.[langKey] ||
                        doc?.documentNameTranslated?.[langKey] ||
                        doc?.documentName ||
                        doc?.bucket_name ||
                        doc?.name}
                    </Button>
                    {doc?.clave && <Badge tone="neutral">Cl@ve</Badge>}
                    {doc?.tick && (
                      <Icon
                        name="checkCircle"
                        size={20}
                        className="data-info__tick"
                      />
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </Drawer>
      <FilePreviewModal
        show={filePreview}
        onClose={() => {
          showFilePreview(false);
        }}
        files={documentGalleryData}
        showIndex={showIndex}
        hideDownload={hideDownload}
      />
    </>
  );
};

export default DataInfoModal;
